// utils/recurringDonationUtils.js
import { DateTime } from 'luxon';

/**
 * Calculate a recurring schedule for a given # of future occurrences.
 *
 * @param {string} timeZone
 * @param {Array} selectedProducts
 * @param {string} currency
 * @param {string} recurrence - one of: 'daily','weekly','monthly','yearly'
 * @param {string} dayValue - e.g. 'friday' or '15', depending on recurrence
 * @param {string} monthValue - e.g. '4' for April if yearly
 * @param {number} occurrences - how many future occurrences to generate (for weekly/monthly/yearly)
 *
 * @returns { donationSchedule, summary }
 */
export const calculateRecurringDonations = ({
  timeZone,
  selectedProducts,
  currency,
  recurrence,
  dayValue,
  monthValue,
  occurrences = 6, // for weekly/monthly/yearly
}) => {
  // 1) Sum up the total from selected products
  const totalExact = selectedProducts.reduce((sum, p) => {
    const price = p[currency.toLowerCase()] || 0;
    return sum + price * (p.quantity || 0);
  }, 0);
  const totalRounded = Math.round(totalExact);

  // If no total, return empty
  if (totalRounded === 0) {
    return {
      donationSchedule: [],
      summary: {
        totalAmount: 0,
        totalShares: 0,
        shareValue: 0,
      },
    };
  }

  // 2) Determine the "start" day. If it’s past 00:30 AM local time, skip "today"
  //    and move to "tomorrow"
  const nowFull = DateTime.now().setZone(timeZone);
  const hour = nowFull.hour;
  const minute = nowFull.minute;
  const isPastMidnight30 = hour > 0 || (hour === 0 && minute > 30);

  let cursor = nowFull.startOf('day');
  if (isPastMidnight30) {
    // skip "today" entirely, jump to next day
    cursor = cursor.plus({ days: 1 });
  }

  // For daily, let’s create 365 days. For other recurrences, we respect occurrences
  let scheduleDates = [];

  if (recurrence === 'daily') {
    // Generate 365 daily entries
    for (let i = 0; i < 365; i++) {
      scheduleDates.push(cursor);
      cursor = cursor.plus({ days: 1 });
    }
  } else {
    // For weekly / monthly / yearly => generate up to `occurrences`
    while (scheduleDates.length < occurrences) {
      switch (recurrence) {
        case 'weekly': {
          const desiredWeekday = mapDayOfWeek(dayValue);
          // move forward until cursor.weekday matches
          while (cursor.weekday !== desiredWeekday) {
            cursor = cursor.plus({ days: 1 });
          }
          scheduleDates.push(cursor);
          // next iteration => +1 week
          cursor = cursor.plus({ weeks: 1 });
          break;
        }

        case 'monthly': {
          const desiredDay = parseInt(dayValue, 10) || 1;
          // if cursor.day > desiredDay, jump to next month
          if (cursor.day > desiredDay) {
            cursor = cursor.plus({ months: 1 }).startOf('month');
          }
          // set to desired day
          cursor = cursor.set({ day: desiredDay });
          scheduleDates.push(cursor);
          // next iteration => +1 month
          cursor = cursor.plus({ months: 1 });
          break;
        }

        case 'yearly': {
          // skip to the correct day+month next year if needed
          const nowLuxon = DateTime.now().setZone(timeZone).startOf('day');
          const desiredDay = parseInt(dayValue, 10) || 1;
          const desiredMonth = parseInt(monthValue, 10) || 1;

          const cursorYear = cursor.year;
          let potentialDate = DateTime.fromObject(
            { year: cursorYear, month: desiredMonth, day: desiredDay },
            { zone: timeZone }
          );

          // if potential date is before "today," jump to next year
          if (potentialDate < nowLuxon) {
            potentialDate = DateTime.fromObject(
              {
                year: cursorYear + 1,
                month: desiredMonth,
                day: desiredDay,
              },
              { zone: timeZone }
            );
          }
          // if potential date is still behind cursor, also jump +1 year
          if (potentialDate < cursor) {
            potentialDate = DateTime.fromObject(
              {
                year: cursorYear + 1,
                month: desiredMonth,
                day: desiredDay,
              },
              { zone: timeZone }
            );
          }

          scheduleDates.push(potentialDate);
          cursor = potentialDate.plus({ years: 1 });
          break;
        }

        default: {
          // fallback => treat as daily for 1 day if unknown?
          scheduleDates.push(cursor);
          cursor = cursor.plus({ days: 1 });
          break;
        }
      }
    }
  }

  // 3) Build the final donationSchedule => each occurrence gets the entire total
  // If you want to "split" the total across each occurrence, you'd do different logic
  let donationSchedule = scheduleDates.map((dt) => ({
    date: dt.toISODate(),
    shares: 1,
    amount: totalRounded,
    isMultiplierNight: false,
  }));

  // daily => 365 items, each is totalRounded => final = 365 * totalRounded
  // weekly/monthly => occurrences items => final = occurrences * totalRounded
  let finalSum;
  if (recurrence === 'daily') {
    finalSum = totalRounded * 365;
  } else {
    finalSum = totalRounded * donationSchedule.length;
  }

  const totalShares = donationSchedule.length;
  const shareValue = totalRounded; // each occurrence is entire total

  // 4) Product-level allocation
  const handleAddPrd = () => {
    const tempSchedule = JSON.parse(JSON.stringify(donationSchedule));
    let totalBaseForProducts = 0;
    selectedProducts.forEach((product) => {
      const productBase = product.quantity * (product[currency.toLowerCase()] || 0);
      totalBaseForProducts += productBase;
    });

    const productsWithPercentage = selectedProducts.map((product) => {
      const productBase = product.quantity * (product[currency.toLowerCase()] || 0);
      return {
        product,
        percentage: totalBaseForProducts
          ? (productBase * 100) / totalBaseForProducts
          : 0,
      };
    });

    const donationScheduleWithProducts = tempSchedule.map((day) => {
      if (day.amount <= 0) return null;
      let initialAllocations = productsWithPercentage.map((item) => {
        const rawQuantity = (day.amount * item.percentage) / 100;
        return {
          product: JSON.parse(JSON.stringify(item.product)),
          rawQuantity,
          quantity: Math.round(rawQuantity),
        };
      });

      // fix rounding
      let sumAlloc = initialAllocations.reduce((acc, x) => acc + x.quantity, 0);
      let diff = day.amount - sumAlloc;
      let idx = 0;
      while (diff !== 0) {
        if (diff > 0) {
          initialAllocations[idx].quantity++;
          diff--;
        } else if (diff < 0 && initialAllocations[idx].quantity > 0) {
          initialAllocations[idx].quantity--;
          diff++;
        }
        idx = (idx + 1) % initialAllocations.length;
      }

      const products = initialAllocations.map((alloc) => {
        alloc.product.quantity = alloc.quantity;
        return alloc.product;
      });

      return {
        date: day.date,
        shares: day.shares,
        amount: day.amount,
        isMultiplierNight: day.isMultiplierNight,
        products,
        currency,
        timeZone,
      };
    });

    const filtered = donationScheduleWithProducts.filter((d) => d);
    localStorage.setItem('RECURRING_DONATION', JSON.stringify(filtered));
  };
  handleAddPrd();

  return {
    donationSchedule,
    summary: {
      totalAmount: finalSum,
      totalShares,
      shareValue,
    },
  };
};

/** A helper to map e.g. "monday" => 1, "tuesday" => 2, etc. */
function mapDayOfWeek(dayStr) {
  switch ((dayStr || '').toLowerCase()) {
    case 'monday':
      return 1;
    case 'tuesday':
      return 2;
    case 'wednesday':
      return 3;
    case 'thursday':
      return 4;
    case 'friday':
      return 5;
    case 'saturday':
      return 6;
    case 'sunday':
      return 7;
    default:
      return 1; // fallback
  }
}
